/**
 * Moderator Join Login Component
 *
 * The way in the portal's moderation page opens for a moderator:
 * ?mode=moderator-join&user=<nakamaCustomId>&name=<name>&cohort=<cohortId>&
 * world=<worldName>&match=<matchId>, read by readLink
 * (domains/entry-links.ts). It waits on the moderator — who chooses when to
 * come into a team's match and its voice — then joins that match by its id
 * as a moderator: never a new match, and never full screen.
 *
 * The card says who is joining — the moderator's own name in the accent, with
 * the MOD chip the rail gives a moderator — and which match, the world in the
 * kicker; the gold Join takes the focus as the page opens, so Enter joins.
 * From the press the join's steps take Join's place (JoinStatus); refused,
 * the refusal is said by its code, with Retry, focused, where a second try
 * can work, and the way back, the portal. A link cut short names what it
 * lacks.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readLink, shortMatch } from '../../domains/entry-links';
import { incompleteLink } from '../../domains/join-refusals';
import { authService } from '../../services/authentication/auth-service';
import { Chip } from '../ui/chip';
import { EntryCaption, EntryCard, EntryFacts, EntryWho, JoinStatus, MissingParams, RefusalNotice } from './entry-card';
import { EntryFrame } from './entry-frame';
import { useJoinState } from './use-join-state';

export function ModeratorJoinLogin({ search }: { search: string }) {
  const { t } = useTranslation();
  const link = useMemo(() => readLink(search, 'moderator-join'), [search]);
  const joinState = useJoinState();
  // The moderator has pressed Join: from then the card is the join's.
  const [pressed, setPressed] = useState(false);

  const frame = { search, title: t('moderatorJoin.title'), meaning: t('moderatorJoin.subtitle') };

  // A link cut short: nothing here can mend it; the portal makes a new one.
  if (link.missing) {
    return (
      <EntryFrame {...frame}>
        <EntryCard>
          <RefusalNotice words={incompleteLink()} detail={<MissingParams names={link.missing} />} />
          <EntryCaption>{t('entry.refusal.reopenFromPortal')}</EntryCaption>
        </EntryCard>
      </EntryFrame>
    );
  }

  const { user, cohort, world, match, name } = link.parts;
  const join = () => {
    setPressed(true);
    void authService.connectAsModerator({ userNakamaCustomId: user, cohortId: cohort, worldId: world, matchId: match, displayName: name });
  };

  return (
    <EntryFrame {...frame} kicker={[world]}>
      <EntryCard>
        <EntryWho label={t('privateChat.moderator')} name={name ?? user} chip={<Chip tone="neutral">{t('rail.player.mod')}</Chip>} />
        <EntryFacts
          facts={[
            { label: t('entry.fact.class'), value: cohort },
            { label: t('entry.fact.match'), value: shortMatch(match) },
          ]}
        />
        <JoinStatus state={pressed ? joinState : { kind: 'ready', label: t('moderatorJoin.joinButton') }} onJoin={join} ask="portal" />
      </EntryCard>
    </EntryFrame>
  );
}
